import {
  GetAdvertiserLocationsForCompany,
  GetBusRequestFromCompany,
  GetAdvertiserRequests,
  GetBussesForCompany,
  GetCompany,
  GetAdvertiserLocations,
  GetRegisteredCompaniesNear,
  GetCurrentSubscriptionForUser,
} from "@/http/LiveHttpService";
import { HttpResponse } from "@/types/http";
import { GeoLocation, GeoLocationWithRadius } from "@/types/location";
import { CompanyType } from "@/types/company";
import { useQuery } from "@tanstack/react-query";
import { EmailSettingsData, EmailSettingsTravelservice } from "@/types/user";
import { autocompleteAddress } from "@/utils/placesApiCalls";
import { ActiveBusRequest, BusRequest } from "@/types/busrequest";
import QueryKeys from "@/utils/tanstack-query/constants/queryKeys";
import { useLocaleContext } from "@/app/i18n/client";
import { isAfter, isBefore } from "date-fns";

// --------------------
// ACHTUNG
//
// die querys müssen erst an eine variable gebunden werden (const query) und diese dann returned
// sonst wird der query nicht direkt gestartet
//
// ACHTUNG
// --------------------

export function useAddressAutocompleteQuery(queryTerm: string, id?: string) {
  const lng = useLocaleContext();
  const query = useQuery({
    queryKey: [id ?? "addressBlock", queryTerm],
    queryFn: async () => {
      if (queryTerm === "") {
        return null;
      }
      const res = await autocompleteAddress(queryTerm, lng, "street_address");
      return res;
    },
  });
  return query;
}

export function useCompanyByIdQuery(
  companyId: string,
  fromSingleRequest?: boolean,
) {
  console.log("Query: " + companyId);
  const query = useQuery({
    queryKey: ["company", companyId, fromSingleRequest],
    queryFn: async () => {
      if (fromSingleRequest) {
        return null;
      }
      const company =
        await GetCompany<Extract<CompanyType, "touroperator" | "busoperator">>(
          companyId,
        );
      if (company.successful) {
        if (company.entity) {
          return company.entity;
        }
      }
      throw new Error(`GetCompany ${companyId} failed`);
    },
  });
  return query;
}

export function useBusRequestFromCompany(companyId: string) {
  const query = useQuery({
    queryKey: ["busRequestFromCompany", companyId],
    queryFn: async () => {
      const result = await GetBusRequestFromCompany(companyId);
      if (result.successful) {
        return result.entity ?? [];
      } else {
        throw new Error(result.error);
      }
    },
  });
  return query;
}

export function useBussesForCompany(companyId: string) {
  const query = useQuery({
    queryKey: ["busses", companyId],
    queryFn: async () => {
      const data = await GetBussesForCompany(companyId);
      if (data.successful) {
        return data.entity ?? [];
      }
      return [];
    },
    placeholderData: [],
  });
  return query;
}

export function useAdvertiserLocations(companyId: string) {
  const query = useQuery({
    queryKey: ["advertiserLocations", companyId],
    queryFn: async () => {
      const data = await GetAdvertiserLocationsForCompany(companyId);
      if (data.successful) {
        return data.entity ?? [];
      }
      if (data.error === "LOC_COUNT_MAX") {
        // TODO: display error popup
      }
      return [];
    },
    placeholderData: [],
  });
  return query;
}

export function useGetCurrentSubscriptionForUserQuery(
  subscriptionId: string | undefined,
) {
  const query = useQuery({
    queryKey: ["stripeLocationAmount", subscriptionId],
    retry: false,
    queryFn: async (context) => {
      if (!subscriptionId) {
        throw new Error("ERRORS.STRIPE.SUBSCRIPTION MISSING");
      }
      try {
        const stripeLocForUser = await GetCurrentSubscriptionForUser(
          subscriptionId ?? "",
          context.signal,
        );
        return stripeLocForUser;
      } catch (er) {
        if (er instanceof Error) {
          throw new Error(er.message, { cause: er.name }); //TODO + t("dataerrors:CONTEXT.LOCATION FOR USER")
        }
      }
    },
  });
  return query;
}

export function useRegisteredCompaniesNearQuery<
  CT extends readonly CompanyType[] | undefined,
>(companyTypes?: CT, locations?: GeoLocationWithRadius[]) {
  const query = useQuery({
    queryKey: [QueryKeys.RegisteredCompanies, companyTypes, locations],
    queryFn: async () => {
      const data = await GetRegisteredCompaniesNear(companyTypes, locations);
      if (data.successful) {
        return data.entity ?? [];
      }
      throw new Error("could not retrieve registered companies");
    },
  });
  return query;
}

export function proccessNearRequests(data: HttpResponse<ActiveBusRequest[]>) {
  if (data.successful) {
    const result = data.entity ?? [];
    console.log("Result Data: " + JSON.stringify(result));
    return [...new Map(result.map((item) => [item.id, item])).values()].sort(
      (a, b) => a.created_at.valueOf() - b.created_at.valueOf(),
    );
  }
  throw new Error("GetAdvertiserRequests failed");
}

export function useNearRequestsQuery(locations: GeoLocation[]) {
  const query = useQuery({
    queryKey: ["nearRequests", locations],
    queryFn: async () => {
      const data = await GetAdvertiserRequests(locations);
      return proccessNearRequests(data);
    },
    placeholderData: [],
  });
  return query;
}

export function useAdvertiserLocationsQuery() {
  const query = useQuery({
    queryKey: ["advertiserLocations", "me"],
    queryFn: async () => {
      const data = await GetAdvertiserLocations();
      if (data.successful) {
        return data.entity ?? [];
      }
      throw new Error("GetAdvertiserRequests failed");
    },
  });
  return query;
}
